import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Toolbox from './Toolbox';
import Canvas from './Canvas';
import PropertiesPanel from './PropertiesPanel';
import FormSettings from './FormSettings';
import { generatePowerShellScript } from './generatePowerShellCode';
import { useAuth } from './AuthContext';
import Navbar from './Navbar';
import { supabase } from './supabaseClient';
import { hasProAccess, canExportPowerShell, getMonthlyExportStatus, incrementMonthlyExportCount } from './utils/subscription';

export default function BuilderPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [components, setComponents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showFormSettings, setShowFormSettings] = useState(false);
  const [formSettings, setFormSettings] = useState({
    title: 'Generated GUI',
    width: 800,
    height: 600,
    backgroundColor: 'White',
    resizable: true,
    maximizable: true,
    windowStartupLocation: 'CenterScreen',
  });
  const [autosaveRestored, setAutosaveRestored] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [loadingSavedGUI, setLoadingSavedGUI] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [loadingExportStatus, setLoadingExportStatus] = useState(false);

  // Load saved GUI from URL parameter
  useEffect(() => {
    const loadSavedGUI = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const guiId = urlParams.get('open');
      
      if (guiId && user) {
        setLoadingSavedGUI(true);
        try {
          const { data, error } = await supabase
            .from('saved_guis')
            .select('*')
            .eq('id', guiId)
            .eq('user_id', user.id)
            .single();
          
          if (error) {
            console.error('Error loading saved GUI:', error);
            alert('Error loading saved GUI. Please try again.');
          } else if (data) {
            console.log('Loading saved GUI:', data);
            setComponents(data.data.components || []);
            setFormSettings(data.data.formSettings || formSettings);
            // Clean up URL
            window.history.replaceState({}, document.title, '/builder');
          }
        } catch (error) {
          console.error('Error loading saved GUI:', error);
          alert('Error loading saved GUI. Please try again.');
        } finally {
          setLoadingSavedGUI(false);
        }
      }
    };

    if (user && !loading) {
      loadSavedGUI();
    }
  }, [user, loading]);

  // Load export status when user changes
  useEffect(() => {
    const loadExportStatus = async () => {
      if (!user) {
        setExportStatus(null);
        return;
      }
      
      setLoadingExportStatus(true);
      try {
        const status = await getMonthlyExportStatus(user);
        setExportStatus(status);
      } catch (error) {
        console.error('Error loading export status:', error);
        setExportStatus({
          can_export: false,
          exports_used: 0,
          exports_remaining: 0,
          reset_date: null,
          is_pro: false,
          error: 'Failed to load export status'
        });
      } finally {
        setLoadingExportStatus(false);
      }
    };

    loadExportStatus();
  }, [user]);



  // Autosave to localStorage for Pro users
  useEffect(() => {
    if (user && hasProAccess(user)) {
      const autosaveKey = `autosave_${user.id}`;
      const data = JSON.stringify({ components, formSettings });
      localStorage.setItem(autosaveKey, data);
    }
  }, [components, formSettings, user]);

  // On mount, restore autosave if available (Pro only)
  useEffect(() => {
    if (user && hasProAccess(user) && !autosaveRestored) {
      const autosaveKey = `autosave_${user.id}`;
      const data = localStorage.getItem(autosaveKey);
      
      if (data) {
        // Check if there are any saved GUIs in the database
        const checkSavedGUIs = async () => {
          try {
            const { data: savedGUIs } = await supabase
              .from('saved_guis')
              .select('data')
              .eq('user_id', user.id)
              .order('updated_at', { ascending: false })
              .limit(1);
            
            // Only offer to restore if there are no saved GUIs
            // or if the autosaved data is different from the last saved GUI
            let shouldOfferRestore = true;
            
            if (savedGUIs && savedGUIs.length > 0) {
              const lastSavedData = savedGUIs[0].data;
              const autosavedData = JSON.parse(data);
              
              // Compare the data to see if they're the same
              const isSameData = JSON.stringify(lastSavedData) === JSON.stringify(autosavedData);
              shouldOfferRestore = !isSameData;
            }
            
            if (shouldOfferRestore) {
              try {
                const parsed = JSON.parse(data);
                // Check if the autosaved data actually has components
                const hasComponents = parsed.components && parsed.components.length > 0;
                
                if (hasComponents) {
                  if (window.confirm('Restore your last autosaved design?')) {
                    setComponents(parsed.components || []);
                    setFormSettings(parsed.formSettings || formSettings);
                  }
                } else {
                  // If autosaved data has no components, clear the autosave
                  localStorage.removeItem(autosaveKey);
                }
              } catch (e) {
                // Ignore parse errors and clear invalid autosave
                localStorage.removeItem(autosaveKey);
              }
            }
          } catch (error) {
            console.error('Error checking saved GUIs:', error);
            // If there's an error checking, don't offer to restore
          }
          
          setAutosaveRestored(true);
        };
        
        checkSavedGUIs();
      } else {
        setAutosaveRestored(true);
      }
    }
  }, [user, autosaveRestored, formSettings]);

  useEffect(() => {
    if (!loading && user === null) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Show loading spinner while loading saved GUI
  if (loadingSavedGUI) {
    return (
      <div className="flex flex-col h-screen bg-gray-100 text-sm font-sans">
        <Navbar />
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <div className="text-gray-600">Loading saved GUI...</div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render the main content if user is not authenticated
  if (!user) {
    return null;
  }

  const handleGeneratePowerShell = async () => {
    if (components.length === 0) {
      alert('Please add some components to the canvas first.');
      return;
    }

    // Check if user can export
    const canExport = await canExportPowerShell(user);
    if (!canExport) {
      if (exportStatus && !exportStatus.is_pro) {
        alert(`You have used your monthly export limit (${exportStatus.exports_used}/1). Your limit will reset on ${new Date(exportStatus.reset_date).toLocaleDateString()}. Upgrade to Pro for unlimited exports.`);
      } else {
        alert('Unable to export. Please try again later.');
      }
      return;
    }

    try {
      // Generate the PowerShell script
      const script = generatePowerShellScript(components, formSettings);
      const blob = new Blob([script], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'generated-gui.ps1';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Increment export count for free users
      const success = await incrementMonthlyExportCount(user);
      if (success) {
        // Refresh export status
        const newStatus = await getMonthlyExportStatus(user);
        setExportStatus(newStatus);
      }
    } catch (error) {
      console.error('Error generating PowerShell:', error);
      alert('Error generating PowerShell script. Please try again.');
    }
  };

  const handleClearCanvas = () => {
    if (window.confirm('Are you sure you want to clear the canvas?')) {
      setComponents([]);
      setSelectedId(null);
    }
  };

  const handleDeleteComponent = (componentId) => {
    setComponents(prevComponents => prevComponents.filter(c => c.id !== componentId));
    setSelectedId(null); // Clear selection after deletion
  };

  // Manual save to Supabase
  const handleManualSave = async () => {
    if (!user) return;
    setSaveStatus('Saving...');
    const baseName = formSettings.title || 'Untitled GUI';
    
    try {
      // Check if we're editing an existing GUI (check URL for open parameter)
      const urlParams = new URLSearchParams(window.location.search);
      const guiId = urlParams.get('open');
      
      let result;
      if (guiId) {
        // Update existing GUI
        result = await supabase
          .from('saved_guis')
          .update({
            name: baseName,
            data: { components, formSettings },
            updated_at: new Date().toISOString(),
          })
          .eq('id', guiId)
          .eq('user_id', user.id);
      } else {
        // Create new GUI - always create a new entry
        // First, check if a GUI with this name already exists
        const { data: existingGUIs } = await supabase
          .from('saved_guis')
          .select('name')
          .eq('user_id', user.id)
          .eq('name', baseName);
        
        let finalName = baseName;
        if (existingGUIs && existingGUIs.length > 0) {
          // If name exists, add a timestamp to make it unique
          const timestamp = new Date().toLocaleString().replace(/[/:]/g, '-').replace(/,/g, '');
          finalName = `${baseName} (${timestamp})`;
        }
        
        result = await supabase
          .from('saved_guis')
          .insert([{
            user_id: user.id,
            name: finalName,
            data: { components, formSettings },
            updated_at: new Date().toISOString(),
          }]);
      }
      
      if (result.error) {
        console.error('Save error:', result.error);
        setSaveStatus('Error saving.');
      } else {
        setSaveStatus('Saved!');
        setTimeout(() => setSaveStatus(''), 2000);
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('Error saving.');
    }
  };

  const selectedComponent = components.find(c => c.id === selectedId);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col h-screen bg-gray-100 text-sm font-sans">
        <Navbar />
        {/* Builder UI only */}
        <div className="flex flex-1">
          <div className="flex flex-col w-full">
            {/* Header */}
            <div className="bg-white border-b px-4 py-2 flex justify-between items-center">
              <div className="flex-1"></div>
              <div className="space-x-2 flex items-center">
                {/* Export status for free users */}
                {user && !hasProAccess(user) && exportStatus && (
                  <div className="text-xs text-gray-600 mr-2">
                    {exportStatus.exports_remaining === -1 ? (
                      <span className="text-green-600">Unlimited exports</span>
                    ) : (
                      <span className={exportStatus.can_export ? 'text-green-600' : 'text-red-600'}>
                        {exportStatus.exports_remaining} export{exportStatus.exports_remaining !== 1 ? 's' : ''} remaining this month
                      </span>
                    )}
                    {exportStatus.reset_date && (
                      <span className="text-gray-500 ml-1">
                        (resets {new Date(exportStatus.reset_date).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                )}
                
                {/* Upgrade link for free users who have used their limit */}
                {user && !hasProAccess(user) && exportStatus && !exportStatus.can_export && (
                  <Link 
                    to="/dashboard" 
                    className="text-xs text-red-600 mr-2 hover:text-red-800 underline cursor-pointer"
                    title="Click to upgrade to Pro plan"
                  >
                    Upgrade to Pro for unlimited exports
                  </Link>
                )}
                
                <button
                  onClick={handleManualSave}
                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  disabled={!user}
                  title="Save your design"
                >
                  Save
                </button>
                {saveStatus && <span className="text-xs text-gray-600">{saveStatus}</span>}
                <button
                  onClick={handleGeneratePowerShell}
                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  disabled={!user || loadingExportStatus || (exportStatus && !exportStatus.can_export)}
                  title={
                    !user 
                      ? 'Please log in to export' 
                      : loadingExportStatus 
                        ? 'Loading export status...'
                        : exportStatus && !exportStatus.can_export 
                          ? 'Monthly export limit reached'
                          : 'Generate PowerShell script'
                  }
                >
                  Generate PowerShell
                </button>
                <button
                  onClick={handleClearCanvas}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Clear Canvas
                </button>
              </div>
            </div>
            {/* Main Content */}
            <div className="flex flex-1 p-4 gap-4">
              <Toolbox />
              <Canvas
                components={components}
                setComponents={setComponents}
                setSelectedId={setSelectedId}
                selectedId={selectedId}
              />
              <div className="flex flex-col space-y-2">
                {/* Toggle Buttons */}
                <div className="flex space-x-1">
                  <button
                    onClick={() => setShowFormSettings(false)}
                    className={`px-3 py-1 text-sm rounded ${
                      !showFormSettings 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Component
                  </button>
                  <button
                    onClick={() => setShowFormSettings(true)}
                    className={`px-3 py-1 text-sm rounded ${
                      showFormSettings 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Form
                  </button>
                </div>
                {/* Panel Content */}
                {showFormSettings ? (
                  <FormSettings
                    formSettings={formSettings}
                    updateFormSettings={setFormSettings}
                  />
                ) : (
                  <PropertiesPanel
                    selectedComponent={selectedComponent}
                    components={components}
                    updateComponent={(updated) => {
                      setComponents(components.map(c => c.id === updated.id ? updated : c));
                    }}
                    onDeleteComponent={handleDeleteComponent}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
} 